import React, {Component} from 'react';
import DateRangeFilter from '../components/DateRangeFilter';
import SecondaryFilterWithLocations from '../components/SecondaryFilterWithLocations';
import CombinedSalesReportSet from '../components/CombinedSalesReportSet';
import NoAccess from '../noAccess';
import {RESOURCES as filterResources} from '../fuseFiltersResourceMap';
import {getTimeZoneOffsetDefault} from '../shared/dateTimeLists';
import {hasReportsAccess} from '../shared/authFunctions';
import {ViewTitle} from 'admin-on-rest/lib/mui';
import {Card, CardText} from 'material-ui/Card';
import Avatar from 'material-ui/Avatar';
import Icon from 'material-ui/svg-icons/editor/pie-chart';

export const fuseCombinedSalesReportsIcon = Icon;

/**
 * Combined Sales Reports class for retrieving and displaying custom sales results
 *
 */
export class CombinedSalesReports extends Component {
    constructor(props) {
        super(props);
        const system = localStorage.getItem('system');
        const offsetDefault = getTimeZoneOffsetDefault();
        this.state = {
            dataProcessed: false,
            filterChoiceFirst: null,
            filterChoiceSecond: null,
            filterType: 'all',
            filterTypeName: null,
            location: {
                id: null,
                name: '',
                city: '',
                state: '',
                test: null,
            },
            selectedEndDate: null,
            selectedOffset: offsetDefault,
            selectedStartDate: null,
            system: system,
            totalFiltersAvailable: 0,
        };
    }

    /**
     * Convert Dates to visual display
     *
     * @param selectedStartDate
     * @param selectedEndDate
     * @return {string}
     */
    displaySelectedDates(selectedStartDate, selectedEndDate) {
        const options = {year: 'numeric', month: 'long', day: 'numeric'};
        let startDate = new Date(selectedStartDate + 'T00:00:00');
        let endDate = new Date(selectedEndDate + 'T00:00:00');
        let startDateStr = startDate.toLocaleDateString('en-US', options);
        let endDateStr = endDate.toLocaleDateString('en-US', options);

        return (
            'Date Range: ' + startDateStr + ' through ' + endDateStr
        )
    }

    /**
     * Handle event props from DateRangeFilter
     *
     * @param type
     * @param value
     */
    handleDateRangeFilterSelect = (type, value) => {
        switch (type) {
            case 'start_date':
                // Set Start Date selection for reports summary component
                this.setState({selectedStartDate: value});
                break;
            case 'end_date':
                // Set End selection for reports summary component
                this.setState({selectedEndDate: value});
                break;
            case 'offset':
                // Set Time Zone Offset selection for reports summary component
                this.setState({selectedOffset: value});
                break;
            default:
                return
        }
    };

    /**
     * Handle event for Combined Sales Filter type selection
     *
     * @param type
     */
    handleFilterTypeSelect = (type) => {
        this.setState({
            filterChoiceFirst: null,
            filterChoiceSecond: null,
            filterType: type.id,
            filterTypeName: type.name,
            totalFiltersAvailable: 0
        });
    };

    /**
     * Handle event for Combined Sales Filter selection
     *
     * @param firstChoice
     * @param secondChoice
     */
    handleFilterOptionSelect = (firstChoice, secondChoice) => {
        let filterChoiceFirst = null;
        if (firstChoice) {
            filterChoiceFirst = firstChoice;
        }
        if (secondChoice === 'none') {
            this.setState({
                filterChoiceFirst: filterChoiceFirst,
                filterChoiceSecond: null,
                totalFiltersAvailable: 1
            });
        } else {
            this.setState({
                filterChoiceFirst: filterChoiceFirst,
                filterChoiceSecond: secondChoice,
                totalFiltersAvailable: 2
            });
        }
    };

    /**
     * Handle event props for Combined Sales data processing
     *
     * @param reportSet
     */
    handleDataProcessed = (reportSet) => {
        this.setState({
            dataProcessed: true
        });
    };

    render() {
        const {
            dataProcessed,
            filterChoiceFirst,
            filterChoiceSecond,
            filterType,
            filterTypeName,
            selectedEndDate,
            selectedStartDate,
            selectedOffset,
            system,
            totalFiltersAvailable,
        } = this.state;

        if (!hasReportsAccess()) return <NoAccess/>;

        const ListTitle = <span className="list-title hide-print">
            <Avatar icon={<Icon/>} className="avatar-title"/>Combined Sales Reports</span>;

        const ListDescription = () => {
            return (
                <span className="list-description hide-print">
                    <p>A Combined Sales Report assembles three reports:</p>
                    <ul>
                        <li>Totals and averages for all Locations</li>
                        <li>List of all Locations</li>
                        <li>Totals by Location</li>
                        <li>Daily Sales for Location per Date Range</li>
                    </ul>
                    <p>Select a Date Range and Time Zone Offset to generate Reports. Then select a secondary
                        filter to further specify results.</p>
                </span>
            );
        };

        let showFilterPrompt = false;
        let showLoading = !!(selectedStartDate && selectedEndDate && selectedOffset);
        if (filterType && filterType !== 'all') {
            if (totalFiltersAvailable === 2 && (!filterChoiceFirst || !filterChoiceSecond)) {
                showFilterPrompt = true;
                showLoading = false;
            } else if (!filterChoiceFirst) {
                showFilterPrompt = true;
                showLoading = false;
            }
        }
        let filterPrompt = (filterTypeName)
            ? 'Please complete a Secondary Filter for ' + filterTypeName.substr(3) + '.'
            : '';
        if (filterType === 'locations') {
            filterPrompt = 'Please add desired Locations and click Generate Report.'
        }

        return (
            <div className="lists-container">
                <Card>
                    <ViewTitle title={ListTitle}/>
                    <CardText>
                        <ListDescription/>

                        {/* Report Filter (Start Date, End Date, Time Zone) */}
                        <DateRangeFilter
                            onSelectStartDate={this.handleDateRangeFilterSelect}
                            onSelectEndDate={this.handleDateRangeFilterSelect}
                            onSelectOffset={this.handleDateRangeFilterSelect}
                        />

                        {/* Secondary Filters */}
                        {dataProcessed &&
                        <SecondaryFilterWithLocations
                            startDate={selectedStartDate}
                            endDate={selectedEndDate}
                            offset={selectedOffset}
                            resource={filterResources.SalesSummaryFilterByItems}
                            onFilterTypeSelect={this.handleFilterTypeSelect}
                            onFilterChoiceSelect={this.handleFilterOptionSelect}
                        />
                        }

                        {/* Prompt Messaging */}
                        {showFilterPrompt &&
                        <div className="loader-container">
                            <h4>{filterPrompt}</h4>
                        </div>
                        }

                        {/* Combined Sales Report Content */}
                        {selectedStartDate && selectedEndDate && selectedOffset &&
                        <CombinedSalesReportSet
                            system={system}
                            startDate={selectedStartDate}
                            endDate={selectedEndDate}
                            filterChoiceFirst={filterChoiceFirst}
                            filterChoiceSecond={filterChoiceSecond}
                            filterType={filterType}
                            offset={selectedOffset}
                            totalFiltersAvailable={totalFiltersAvailable}
                            showLoading={showLoading}
                            onDataProcessed={this.handleDataProcessed}
                        />
                        }
                    </CardText>
                </Card>
            </div>
        )
    }
}



// WEBPACK FOOTER //
// ./src/Fuse/Report/CombinedSalesReports.js